import {
  Fragment,
  type MouseEvent,
  forwardRef,
  useCallback,
  useMemo,
  useState,
} from 'react';
import type React from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Box, Collapse, IconButton } from '@mui/material';
import {
  type GridColDef,
  type GridEventListener,
  type GridRenderCellParams,
  GridRow,
  type GridRowId,
  type GridRowProps,
  type GridValidRowModel,
} from '@mui/x-data-grid';
import { useIsMobile } from '@core/hooks/useIsMobile';
import type { ContextMenuItem, ContextMenuParams } from '../ContextMenu';
import {
  type RowDetailContextValue,
  useRowDetailContext,
} from './RowDetailContext';

type RowMenuState = {
  rowId: string;
  field: string;
  anchorEl?: HTMLElement;
  mouseX?: number;
  mouseY?: number;
};

const trailingCellSx = {
  row: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    minWidth: 0,
    gap: 0.5,
  },
  content: {
    flex: '1 1 auto',
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  controls: {
    display: 'flex',
    flexShrink: 0,
    alignItems: 'center',
  },
} as const;

const RowWithDetails = forwardRef<HTMLDivElement, GridRowProps>(
  function RowWithDetails(props, ref) {
    const detail = useRowDetailContext();
    if (!detail) {
      return <GridRow ref={ref} {...props} />;
    }
    return (
      <Fragment key={props.rowId}>
        <GridRow ref={ref} {...props} />
        <Collapse
          in={detail.expandedRowId === props.rowId}
          timeout="auto"
          unmountOnExit
        >
          <Box
            sx={{
              width: props.columnsTotalWidth,
              boxSizing: 'border-box',
              borderBottom: 1,
              borderColor: 'divider',
              bgcolor: (theme) =>
                theme.palette.mode === 'light' ? 'grey.50' : 'action.hover',
            }}
          >
            <Box sx={{ px: 2, py: 1.5, maxWidth: '100%' }}>
              {detail.getRowDetails(props.row)}
            </Box>
          </Box>
        </Collapse>
      </Fragment>
    );
  }
);

function TrailingButtons({
  params,
  expanded,
  showExpand,
  showMenu,
  onToggleExpand,
  onOpenMenu,
}: {
  params: GridRenderCellParams;
  expanded: boolean;
  showExpand: boolean;
  showMenu: boolean;
  onToggleExpand: (id: GridRowId) => void;
  onOpenMenu: (anchorEl: HTMLElement, rowId: string, field: string) => void;
}) {
  const stopRowClick = (event: MouseEvent) => event.stopPropagation();
  return (
    <>
      {showExpand ? (
        <IconButton
          aria-label={expanded ? 'Collapse row details' : 'Expand row details'}
          aria-expanded={expanded}
          size="small"
          sx={{ mr: showMenu ? -0.25 : -0.5 }}
          onClick={(event) => {
            stopRowClick(event);
            onToggleExpand(params.id);
          }}
        >
          <ExpandMoreIcon
            sx={{
              transform: expanded ? 'rotate(180deg)' : 'none',
              transition: (theme) =>
                theme.transitions.create('transform', {
                  duration: theme.transitions.duration.shortest,
                }),
            }}
          />
        </IconButton>
      ) : null}
      {showMenu ? (
        <IconButton
          aria-label="Row actions"
          size="small"
          sx={{ mr: -0.5 }}
          onClick={(event) => {
            stopRowClick(event);
            onOpenMenu(event.currentTarget, String(params.id), params.field);
          }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      ) : null}
    </>
  );
}

function appendTrailingToLastColumn<R extends GridValidRowModel>(
  columns: GridColDef<R>[],
  renderTrailing: (params: GridRenderCellParams<R>) => React.ReactNode
): GridColDef<R>[] {
  const lastIndex = columns.length - 1;
  if (lastIndex < 0) {
    return columns;
  }

  const lastColumn = columns[lastIndex];
  const baseRenderCell = lastColumn.renderCell;

  const enhancedLast: GridColDef<R> = {
    ...lastColumn,
    renderCell: (params) => {
      const content = baseRenderCell
        ? baseRenderCell(params)
        : (params.formattedValue as React.ReactNode);
      return (
        <Box sx={trailingCellSx.row}>
          <Box sx={trailingCellSx.content}>{content}</Box>
          <Box
            {...{ 'data-row-trailing-control': '' }}
            sx={trailingCellSx.controls}
          >
            {renderTrailing(params)}
          </Box>
        </Box>
      );
    },
  };

  return lastIndex === 0
    ? [enhancedLast]
    : [...columns.slice(0, lastIndex), enhancedLast];
}

type UseDataTableRowsOptions<R extends GridValidRowModel> = {
  columns: GridColDef<R>[];
  getRowDetails?: (row: R) => React.ReactNode;
  onRowClick?: GridEventListener<'rowClick'>;
  contextMenuItems?: (params: ContextMenuParams) => ContextMenuItem[];
};

export function useDataTableRows<R extends GridValidRowModel>({
  columns,
  getRowDetails,
  onRowClick,
  contextMenuItems,
}: UseDataTableRowsOptions<R>) {
  const isMobile = useIsMobile();
  const [expandedRowId, setExpandedRowId] = useState<GridRowId | null>(null);
  const [rowMenu, setRowMenu] = useState<RowMenuState | null>(null);

  const hasRowDetails = Boolean(getRowDetails);
  const hasRowClick = Boolean(onRowClick) || hasRowDetails;
  const hasContextMenu = Boolean(contextMenuItems);
  const showExpand = hasRowDetails && (!hasContextMenu || !isMobile);
  const showMenu = hasContextMenu && isMobile;

  const toggleExpanded = useCallback((id: GridRowId) => {
    setExpandedRowId((prev) => (prev === id ? null : id));
  }, []);

  const detailContext = useMemo(
    (): RowDetailContextValue | null =>
      getRowDetails
        ? {
            expandedRowId,
            toggleExpanded,
            getRowDetails: getRowDetails as (
              row: GridValidRowModel
            ) => React.ReactNode,
          }
        : null,
    [expandedRowId, getRowDetails, toggleExpanded]
  );

  const handleRowClick = useCallback<GridEventListener<'rowClick'>>(
    (params, event, details) => {
      if (
        (event.target as HTMLElement).closest('[data-row-trailing-control]')
      ) {
        return;
      }
      if (onRowClick) {
        onRowClick(params, event, details);
        return;
      }
      if (hasRowDetails) {
        toggleExpanded(params.id);
      }
    },
    [onRowClick, hasRowDetails, toggleExpanded]
  );

  const slots = useMemo(
    () => (hasRowDetails ? { row: RowWithDetails } : undefined),
    [hasRowDetails]
  );

  const openMenuFromButton = useCallback(
    (anchorEl: HTMLElement, rowId: string, field: string) => {
      if (!contextMenuItems) return;
      setRowMenu({ rowId, field, anchorEl });
    },
    [contextMenuItems]
  );

  const gridColumns = useMemo(() => {
    if (!showExpand && !showMenu) {
      return columns;
    }
    return appendTrailingToLastColumn(columns, (params) => (
      <TrailingButtons
        params={params}
        expanded={expandedRowId === params.id}
        showExpand={showExpand}
        showMenu={showMenu}
        onToggleExpand={toggleExpanded}
        onOpenMenu={openMenuFromButton}
      />
    ));
  }, [
    columns,
    expandedRowId,
    openMenuFromButton,
    showExpand,
    showMenu,
    toggleExpanded,
  ]);

  const isRowExpanded = useCallback(
    (rowId: GridRowId) =>
      expandedRowId != null && String(expandedRowId) === String(rowId),
    [expandedRowId]
  );

  const isRowMenuOpen = useCallback(
    (rowId: GridRowId) => rowMenu?.rowId === String(rowId),
    [rowMenu?.rowId]
  );

  const handleContextMenu = useCallback(
    (event: React.MouseEvent) => {
      if (!contextMenuItems) return;

      const cell = (event.target as HTMLElement).closest(
        '.MuiDataGrid-cell[data-field]'
      );
      const row = (event.target as HTMLElement).closest(
        '.MuiDataGrid-row[data-id]'
      );
      if (!cell || !row) return;

      const rowId = row.getAttribute('data-id');
      const field = cell.getAttribute('data-field');
      if (rowId == null || field == null || rowId === 'gridPanelAnchor') {
        return;
      }

      event.preventDefault();
      setRowMenu({
        rowId,
        field,
        mouseX: event.clientX,
        mouseY: event.clientY,
      });
    },
    [contextMenuItems]
  );

  const closeMenu = useCallback(() => setRowMenu(null), []);

  const menuItems = useMemo((): ContextMenuItem[] => {
    if (!rowMenu || !contextMenuItems) {
      return [];
    }
    return contextMenuItems({
      rowId: rowMenu.rowId,
      field: rowMenu.field,
    });
  }, [contextMenuItems, rowMenu]);

  const menuAnchor = rowMenu
    ? {
        anchorReference: (rowMenu.anchorEl ? 'anchorEl' : 'anchorPosition') as
          | 'anchorEl'
          | 'anchorPosition',
        anchorEl: rowMenu.anchorEl,
        anchorPosition: rowMenu.anchorEl
          ? undefined
          : { top: rowMenu.mouseY ?? 0, left: rowMenu.mouseX ?? 0 },
      }
    : null;

  return {
    columns: gridColumns,
    slots,
    handleRowClick,
    hasRowClick,
    detailContext,
    isRowExpanded,
    isRowMenuOpen,
    handleContextMenu,
    closeMenu,
    menuItems,
    menuAnchor,
    isMenuOpen: Boolean(rowMenu && menuItems.length > 0),
  };
}
